import { useState, useEffect, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { filterAvailableFilters } from 'utils/filterPermissions';
import { globalSnackbar } from 'components/GlobalSnackbar';
import { extractReuploadFileNames } from 'utils/reuploadError';
import { calcUsage } from '../api';
import type { SourceFiltersState } from './use-chat-filters';

interface UsageData {
  sum: number;
  max: number;
  percentage: number;
}

export const useContextUsage = (
  sourceFilters: SourceFiltersState | null,
  humanMessage: string,
  expandedGeneralInfo: boolean,
  getContextMessagesLimit: () => number | null,
  chatId: string | null
) => {
  const currProject = useSelector(
    (state: { reducer: { project: { id: string }; role?: { id: number | null } & unknown } }) =>
      state.reducer.project.id
  );
  const roleId = useSelector(
    (state: { reducer: { role?: { id: number | null } } }) => state.reducer.role?.id ?? null
  );
  const userRole = useSelector((state: { reducer: { role?: unknown } }) => state.reducer.role);
  const gitContextVersion = useSelector(
    (state: { reducer: { gitContextVersion: number } }) => state.reducer.gitContextVersion
  );

  const [usageData, setUsageData] = useState<UsageData>({
    sum: 0,
    max: 160000,
    percentage: 0,
  });
  const [loading, setLoading] = useState(false);
  const [reuploadFileNames, setReuploadFileNames] = useState<string[] | null>(null);
  const prevParamsRef = useRef<string | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasCalledRef = useRef(false);

  const valuesRef = useRef({
    sourceFilters,
    currProject,
    roleId,
    userRole,
    humanMessage,
    expandedGeneralInfo,
    getContextMessagesLimit,
    chatId,
  });

  useEffect(() => {
    valuesRef.current = {
      sourceFilters,
      currProject,
      roleId,
      userRole,
      humanMessage,
      expandedGeneralInfo,
      getContextMessagesLimit,
      chatId,
    };
  }, [
    sourceFilters,
    currProject,
    roleId,
    userRole,
    humanMessage,
    expandedGeneralInfo,
    getContextMessagesLimit,
    chatId,
  ]);

  const filtersKey = useMemo(() => {
    if (!sourceFilters) return null;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { show_specification_select, context_mode, ...filters } = sourceFilters;
    return JSON.stringify({
      ...filters,
      use_source_common: expandedGeneralInfo ? filters.use_source_common : false,
      use_context_files: expandedGeneralInfo ? filters.use_context_files : [],
      context_messages_limit: getContextMessagesLimit ? getContextMessagesLimit() : null,
    });
  }, [sourceFilters, expandedGeneralInfo, getContextMessagesLimit]);

  const paramsKey = useMemo(() => {
    if (!currProject || !roleId || !filtersKey || !chatId) return null;
    return JSON.stringify({
      currProject,
      roleId,
      filtersKey,
      humanMessage: humanMessage || '',
      gitContextVersion,
      chatId,
    });
  }, [currProject, roleId, filtersKey, humanMessage, gitContextVersion, chatId]);

  useEffect(() => {
    if (!sourceFilters || !currProject || !roleId || !paramsKey || !chatId) return;

    const paramsChanged = prevParamsRef.current !== paramsKey;
    if (!hasCalledRef.current || paramsChanged) {
      hasCalledRef.current = true;
      prevParamsRef.current = paramsKey;

      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      timeoutRef.current = setTimeout(async () => {
        const currentValues = valuesRef.current;
        const resolvedChatId = currentValues.chatId;
        if (
          !currentValues.sourceFilters ||
          !currentValues.currProject ||
          currentValues.roleId == null ||
          !resolvedChatId
        )
          return;

        try {
          const filteredFilters = filterAvailableFilters(
            currentValues.sourceFilters,
            currentValues.userRole as object
          ) as Record<string, unknown>;
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { show_specification_select, context_mode, ...filters } = filteredFilters;
          const requestData = {
            human_message: currentValues.humanMessage || '',
            ...filters,
            use_source_common: currentValues.expandedGeneralInfo
              ? filters.use_source_common
              : false,
            use_context_files: currentValues.expandedGeneralInfo ? filters.use_context_files : [],
            context_messages_limit: currentValues.getContextMessagesLimit?.() ?? null,
          };

          setLoading(true);
          const response = await calcUsage(resolvedChatId, currentValues.roleId, requestData);
          if (response) {
            setReuploadFileNames(null);
            setUsageData({
              sum: response.sum ?? 0,
              max: response.max ?? 160000,
              percentage: response.percentage ?? 0,
            });
          }
        } catch (err) {
          const reuploadNames = extractReuploadFileNames(err);
          if (reuploadNames) {
            // Авто-вызов (debounce) не должен открывать модалку — показываем
            // предупреждение в usage-блоке, диалог откроет юзер кнопкой.
            setReuploadFileNames(reuploadNames);
          } else {
            console.warn('Ошибка калькуляции контекстного лимита:', err);
            globalSnackbar({
              message: (err as Error).message || 'Ошибка при расчете контекстного лимита',
              severity: 'error',
            });
          }
        } finally {
          setLoading(false);
        }
      }, 1000);
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  return { usageData, loading, reuploadFileNames };
};
